<script setup lang="ts">
  import logger from '@leaflink/snitch';
  import { computed, inject, nextTick, onMounted, toRefs, useCssModule } from 'vue';
  import { type RouteLocationRaw, type RouterLinkProps } from 'vue-router';

  import Badge from '../Badge/Badge.vue';
  import { TABS_INJECTION } from '../Tabs/Tabs.vue';

  /**
   * Allowed attributes for the `<a>` element when the `href` prop is used.
   */
  type AnchorAttrs =
    | HTMLAnchorElement['download']
    | HTMLAnchorElement['hreflang']
    | HTMLAnchorElement['ping']
    | HTMLAnchorElement['referrerPolicy']
    | HTMLAnchorElement['rel']
    | HTMLAnchorElement['target']
    | HTMLAnchorElement['type'];

  export interface TabProps {
    /**
     * The tab identifier
     */
    value: string;

    /**
     * Router link `to` prop
     */
    to?: RouteLocationRaw;

    /**
     * Anchor tag href. Used for navigating to non-vue pages, such as Django pages in marketplace.
     */
    href?: string;

    /**
     * The badge's content
     */
    badge?: string | number;

    /**
     * Will render a plain text item if true.
     */
    disabled?: boolean;

    /**
     * Props for the `<a>` element when the `href` prop is used
     */
    anchorProps?: Record<AnchorAttrs, string>;

    /**
     * Props for the `<RouterLink>` element when the `to` prop is used
     */
    routerLinkProps?: Omit<RouterLinkProps, 'to'>;
  }

  const props = defineProps<TabProps>();

  const classes = useCssModule();

  const tabsInjection = inject(TABS_INJECTION.key);

  if (!tabsInjection) {
    throw Error('The Tab component must be a child of the Tabs component.');
  }

  const { setActiveTab, activeTab, variant } = tabsInjection;

  const isTabActive = computed(() => {
    return props.value === activeTab.value;
  });

  const is = computed(() => {
    if (props.to) {
      return 'router-link';
    }

    if (props.href) {
      return 'a';
    }

    return 'button';
  });

  /**
   * Note: this click handler gets ignored when the `href` prop is used; `preventDefault()` is intentionally omitted in order to enable the `href` prop to work correctly. The `href` prop is used for navigation to non-vue pages, such as Django pages in marketplace.
   */
  function onClick(tabValue: string) {
    if (props.disabled) {
      return;
    }

    setActiveTab(tabValue);
  }

  const panelId = computed(() => `tabpanel-${props.value}`);

  onMounted(async () => {
    await nextTick();

    if (process.env.NODE_ENV !== 'test' && isTabActive.value && !document.getElementById(panelId.value)) {
      // The following warning ensures optimal accessibility is maintained when using the Tabs component. See: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tab_role#example
      logger.warn(
        `The <Tab> with value "${props.value}" is active, but its corresponding tab panel cannot be found. Please ensure that the corresponding tab panel has an "id" attribute with value "${panelId.value}" and a "role" attribute with value "tabpanel".`,
      );
    }
  });

  const dynamicComponentAttrs = computed(() => {
    if (props.to && props.routerLinkProps) {
      return toRefs(props.routerLinkProps);
    }

    if (props.href && props.anchorProps) {
      return toRefs(props.anchorProps);
    }

    return {};
  });
</script>

<template>
  <li
    :id="`tab-${props.value}`"
    role="tab"
    :aria-selected="isTabActive"
    :aria-controls="panelId"
    :aria-disabled="props.disabled"
    class="stash-tab"
    :class="[
      {
        'stash-tab--active is-active': isTabActive,
        'stash-tab--disabled': props.disabled,
        'stash-tab--variant-line': variant === 'line',
        'stash-tab--variant-enclosed': variant === 'enclosed',
      },
    ]"
    @click="onClick(props.value)"
    @keypress.enter="onClick(props.value)"
  >
    <component
      v-bind="dynamicComponentAttrs"
      :is="is"
      :to="props.to"
      :href="props.href"
      class="relative flex cursor-pointer items-center justify-center whitespace-nowrap border-solid py-1.5 text-sm font-medium hover:no-underline"
      :class="[
        {
          'border-b-2': variant === 'line',
          'border-b border-t-4 px-6': variant === 'enclosed',
          'rounded-t border-b-ice-200 bg-white': isTabActive && variant === 'enclosed',
          [classes['custom-shadow']]: isTabActive && variant === 'enclosed',
          'focus:no-underline': props.href,
          'pointer-events-none text-ice-500': props.disabled,
          'text-ice-700': !props.disabled && !isTabActive,
        },
        [isTabActive ? 'border-blue-500 text-blue-500' : 'border-transparent hover:text-blue-500 focus:text-blue-500'],
      ]"
      :tabindex="props.disabled ? -1 : 0"
    >
      <!-- The margin-top on the <div> below is necessary to fix the vertical alignment of text in the default slot since our Sofia font has extra space under the baseline -->
      <div class="mt-0.5">
        <Badge :content="props.badge" position="inline" color="red" :is-disabled="props.disabled">
          <slot :is-active="isTabActive" :is-disabled="props.disabled" :variant="variant"></slot>
        </Badge>
      </div>
    </component>
  </li>
</template>

<style module>
  @layer utilities {
    /* this removes the bottom shadow from the active tab */
    .custom-shadow {
      box-shadow: 0 -4px 4px rgb(0 0 0 / 10%);
    }
  }
</style>
